import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import * as React from "react";
import { lazy, Suspense } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import { useCustomPromptsQuery } from "@hooks/useCustomPrompts";
import { useWorkingDocumentId } from "@hooks/useWorkingDocumentId";

// components
import { SessionExpiredOverlay } from "@components/auth/SessionExpiredOverlay";
import { useReauthentication } from "@components/auth/useReauthentication";
import { SpinnerWithBackdrop } from "@components/SpinnerWithBackdrop";
import { Header } from "./Header";

import { WithEulaAcceptance } from "@components/eula";
import { SessionCreationFlowProvider } from "@components/sessionManagment/SessionCreationFlow";
import { UpdateBanner } from "@components/UpdateBanner";
import { useSessions } from "@hooks/useSessions";
import { useStyles } from "./App.styles";

// Lazy-loaded route components for code splitting
const Chat = lazy(() => import("./Chat/Chat"));
const Documents = lazy(() => import("./Documents"));
const Diagrams = lazy(() => import("./Diagrams"));
const Policies = lazy(() => import("./Policies/Policies"));
const Settings = lazy(() => import("./Settings"));
const CustomPrompts = lazy(() => import("./CustomPrompts"));
const SessionLoadScreen = lazy(() => import("./SessionLoadScreen"));

export const App: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { needsReauth, isReauthenticating, handleReauthenticate } = useReauthentication();

  const { isLoading: isLoadingWorkingDocumentId } = useWorkingDocumentId();
  const { isLoading: isLoadingPrompt } = useCustomPromptsQuery();
  const { session, isLoadingSession } = useSessions();

  const isLoadingMutations = useIsMutating({ predicate: (m) => m.meta?.withAppLoading === true }) > 0;
  const isLoadingQueries = useIsFetching({ predicate: (q) => q.meta?.withAppLoading === true }) > 0;
  const isLoadingApp =
    isLoadingWorkingDocumentId || isLoadingSession || isLoadingPrompt || isLoadingMutations || isLoadingQueries;

  return (
    <WithEulaAcceptance>
      <SessionCreationFlowProvider>
        <div className={styles.root}>
          <UpdateBanner />
          <Header onScreenChange={(target) => navigate(`/${target.toLowerCase()}`)} />
          <div className={styles.screen}>
            {isLoadingApp && <SpinnerWithBackdrop />}
            <Suspense fallback={<SpinnerWithBackdrop />}>
              <Routes>
                {!session && <Route path="/" element={<SessionLoadScreen />} />}
                <Route path="/chat" element={<Chat />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/diagrams" element={<Diagrams />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="/custom-prompts" element={<CustomPrompts />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Chat />} />
              </Routes>
            </Suspense>
            {needsReauth && (
              <SessionExpiredOverlay isAuthenticating={isReauthenticating} onSignIn={handleReauthenticate} />
            )}
          </div>
        </div>
      </SessionCreationFlowProvider>
    </WithEulaAcceptance>
  );
};
